import first from "lodash/first";
import { graphql } from "react-relay";
import { readInlineData } from "relay-runtime";

import { article as articleData, section as sectionData } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { Logo } from "scmp-app/components/home/term-logo/styles";
import type { helpersGetArticleLogoArticle$key } from "scmp-app/queries/__generated__/helpersGetArticleLogoArticle.graphql";
import type { helpersGetArticleTitleArticle$key } from "scmp-app/queries/__generated__/helpersGetArticleTitleArticle.graphql";
import type { helpersGetPodcastTitlePodcast$key } from "scmp-app/queries/__generated__/helpersGetPodcastTitlePodcast.graphql";
import type { helpersGetVideoTitleVideo$key } from "scmp-app/queries/__generated__/helpersGetVideoTitleVideo.graphql";

import { LogoContainer, StyledBaseLink } from "./styles";

export const getVideoTitle = (reference?: helpersGetVideoTitleVideo$key) => {
  const video = readInlineData(
    graphql`
      fragment helpersGetVideoTitleVideo on Video @inline {
        sections {
          value {
            entityId
          }
        }
      }
    `,
    reference ?? null,
  );

  if (!notEmpty(video)) return;

  const sections = first(video?.sections);
  const sectionIds = (sections?.value ?? []).map(section => section?.entityId);
  const hasSCMPOriginals = sectionIds.includes(sectionData?.scmpOriginals?.entityId);

  return hasSCMPOriginals ? "SCMP Originals" : "Videos";
};

export const getPodcastTitle = (reference?: helpersGetPodcastTitlePodcast$key) => {
  const podcast = readInlineData(
    graphql`
      fragment helpersGetPodcastTitlePodcast on Podcast @inline {
        __typename
      }
    `,
    reference ?? null,
  );

  return podcast?.__typename === "Podcast" ? "PODCASTS" : undefined;
};

export const getArticleTitle = (reference?: helpersGetArticleTitleArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersGetArticleTitleArticle on Article @inline {
        types {
          value {
            entityId
          }
        }
        sections {
          value {
            entityId
          }
        }
      }
    `,
    reference ?? null,
  );

  if (!notEmpty(article)) return;

  const articleType = first(first(article?.types)?.value)?.entityId ?? "";

  if (articleType) {
    switch (articleType) {
      case articleData.types.blogs.entityId:
      case articleData.types.column.entityId:
      case articleData.types.debate.entityId:
      case articleData.types.opinion.entityId:
      case articleData.types.review.entityId:
        return "COMMENT";
      case articleData.types.infographic.entityId:
        return "Infographics";
      case articleData.types.live.entityId:
        return "Live";
      case articleData.types.series.entityId:
        return "SCMP Series";
    }
  }

  const sections = first(article?.sections);
  const sectionIds = (sections?.value ?? []).map(section => section?.entityId);
  const hasInfographic = sectionIds.includes(sectionData?.infographics?.entityId);

  return hasInfographic ? "INFOGRAPHICS" : undefined;
};

export const getArticleLogo = (reference?: helpersGetArticleLogoArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersGetArticleLogoArticle on Article @inline {
        sections {
          value {
            entityId
            name
            urlAlias
            image(filter: { type: TERM_LOGO }) {
              style(filter: { style: "100x100" }) {
                url
              }
            }
          }
        }
      }
    `,
    reference ?? null,
  );

  if (!article?.sections) return null;

  for (const item of article?.sections) {
    for (const section of item?.value ?? []) {
      if (section?.image?.style?.url) {
        return (
          <LogoContainer>
            <Logo alt={section.name} src={section.image.style.url} />
            <StyledBaseLink pathname={section.urlAlias}>{section.name}</StyledBaseLink>
          </LogoContainer>
        );
      }
    }
  }

  return null;
};
